import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";
import { X, CheckCircle, CalendarBlank, Path, CreditCard, Crown } from "@phosphor-icons/react";

export function SubscriptionPanel({ onClose, onChange, embedded }) {
  const { user } = useAuth();
  const isAdditional = !!user?.is_additional;
  const [info, setInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [me, pl] = await Promise.all([api.get("/subscription/me"), api.get("/plans")]);
      setInfo(me.data); setPlans(pl.data);
      onChange?.(me.data);
    } catch (err) {
      logger.warn("[driver] subscription panel load failed", err);
      setMsg("No se pudo cargar la información de suscripción. Reintenta más tarde.");
    }
  }, [onChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const requestPlan = async (planId) => {
    setBusy(true); setMsg("");
    try {
      await api.post("/subscription/request", { plan_id: planId });
      setMsg("Solicitud enviada. Un administrador la aprobará.");
      await refresh();
    } catch (err) { setMsg(err.response?.data?.detail || "No se pudo solicitar."); }
    finally { setBusy(false); }
  };

  const payPlan = async (planId) => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/subscription/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      setMsg(err.response?.data?.detail || "No se pudo iniciar el pago.");
      setBusy(false);
    }
  };

  const sub = info?.subscription;
  const pending = info?.pending;

  const body = (
    <>
        {/* Current status */}
        {sub ? (
          <div className="rounded-2xl p-4 mb-4" style={{ background: "var(--primary-soft)" }} data-testid="current-subscription">
            <div className="flex items-center justify-between">
              <span className="font-bold">{sub.plan_snapshot?.name}</span>
              <span className="pill pill-online">Activa</span>
            </div>
            <div className="text-sm text-slate-700 mt-1">
              {sub.billing_type === "per_trips"
                ? <>Te quedan <strong>{sub.trips_remaining}</strong> viajes</>
                : <>Vence el <strong>{new Date(sub.expires_at).toLocaleDateString()}</strong></>}
            </div>
          </div>
        ) : pending ? (
          <div className="rounded-2xl p-4 mb-4 bg-amber-50" data-testid="pending-subscription">
            <span className="pill pill-warn">Pendiente de aprobación</span>
            <div className="text-sm text-slate-700 mt-1">Solicitaste <strong>{pending.plan_snapshot?.name}</strong>. Espera la aprobación del administrador o paga con tarjeta para activarla al instante.</div>
          </div>
        ) : (
          info?.subscriptions_required ? (
            <div className="rounded-2xl p-4 mb-4 bg-red-50 text-red-700" data-testid="no-subscription-warning">
              No tienes una suscripción activa. <strong>No recibirás viajes</strong> hasta activar un plan.
            </div>
          ) : (
            <div className="rounded-2xl p-4 mb-4 bg-slate-50 text-slate-600 text-sm" data-testid="subscription-optional-note">
              Las suscripciones no son obligatorias actualmente, así que puedes operar sin un plan. Aun así, puedes activar uno cuando quieras.
            </div>
          )
        )}

        {msg && <div className="p-2 rounded-lg bg-slate-100 text-slate-700 text-sm mb-3" data-testid="subscription-msg">{msg}</div>}

        {/* Available plans */}
        <h3 className="font-display font-bold text-lg mb-2">Planes disponibles</h3>
        <div className="space-y-3">
          {plans.length === 0 && <p className="text-slate-500 text-sm">No hay planes disponibles por ahora.</p>}
          {plans.map((p) => (
            <div key={p.id} className="card" data-testid={`driver-plan-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-bold">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.description}</div>
                  <div className="flex items-center gap-1 text-sm text-slate-600 mt-1">
                    {p.billing_type === "monthly"
                      ? <><CalendarBlank size={14} weight="duotone" /> Mensual · {p.duration_days} días</>
                      : <><Path size={14} weight="duotone" /> {p.trips_included} viajes</>}
                  </div>
                </div>
                <div className="text-xl font-display font-black" style={{ color: "var(--primary)" }}>€{Number(p.price).toFixed(2)}</div>
              </div>
              {isAdditional ? (
                <p className="text-xs text-slate-500 mt-3" data-testid="additional-subscription-note">Solo el conductor titular puede gestionar la suscripción.</p>
              ) : (
              <div className="flex gap-2 mt-3">
                <button disabled={busy} onClick={() => requestPlan(p.id)} className="btn-ghost flex-1 text-sm" data-testid={`request-plan-${p.id}`}>
                  <CheckCircle size={16} className="inline mr-1" weight="duotone" /> Solicitar
                </button>
                {p.price > 0 && (
                  <button disabled={busy} onClick={() => payPlan(p.id)} className="btn-primary flex-1 text-sm" data-testid={`pay-plan-${p.id}`}>
                    <CreditCard size={16} className="inline mr-1" weight="duotone" /> Pagar con tarjeta
                  </button>
                )}
              </div>
              )}
            </div>
          ))}
        </div>
    </>
  );

  if (embedded) {
    return (
      <div className="app-section" data-testid="subscription-section">
        <div className="flex items-center gap-2 mb-4">
          <Crown size={26} weight="duotone" style={{ color: "var(--primary)" }} />
          <h2 className="section-title font-display" style={{ marginBottom: 0 }}>Mi suscripción</h2>
        </div>
        {body}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-[1100] bg-slate-900/50 flex items-center justify-center p-4" data-testid="subscription-panel">
      <div className="bg-white rounded-3xl p-6 w-full max-w-lg fade-in max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Crown size={24} weight="duotone" style={{ color: "var(--primary)" }} /> Mi suscripción</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center" data-testid="subscription-close-btn"><X size={18} /></button>
        </div>
        {body}
      </div>
    </div>
  );
}
